import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import LogoFull from '../../components/ui/LogoFull';
import HeroSection from './components/HeroSection';
import FeatureHighlights from './components/FeatureHighlights';
import AuthCallToAction from './components/AuthCallToAction';
import SocialProof from './components/SocialProof';
import TrustIndicators from './components/TrustIndicators';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';

const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    // Check if user is already logged in and redirect appropriately
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      switch (userData?.role) {
        case 'admin': case'manager': navigate('/analytics-dashboard');
          break;
        case 'employee': navigate('/employee-portal');
          break;
        case 'auditor': navigate('/compliance-reports');
          break;
        default:
          navigate('/analytics-dashboard');
      }
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <LogoFull variant="navy" size="md" tagline={t('brand.tagline')} />

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-muted-foreground hover:text-accent transition-smooth"
              >
                {t('landing.nav.features')}
              </button>
              <button
                onClick={() => scrollToSection('compliance')}
                className="text-muted-foreground hover:text-accent transition-smooth"
              >
                {t('auditor.tabs.compliance')}
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-muted-foreground hover:text-accent transition-smooth"
              >
                {t('landing.nav.pricing')}
              </button>
              <button
                onClick={() => scrollToSection('support')}
                className="text-muted-foreground hover:text-accent transition-smooth"
              >
                {t('loginPage.support')}
              </button>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-foreground hover:bg-muted rounded-md transition-smooth" aria-label="Toggle menu"><Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} /></button>
              <LanguageSwitcher />
              <button
                onClick={() => navigate('/login')}
                className="hidden sm:inline-block px-4 py-2 text-foreground hover:text-accent font-medium transition-smooth whitespace-nowrap"
              >
                {t('landing.nav.signIn')}
              </button>
              <button
                onClick={() => navigate('/login')}
                className="hidden sm:inline-block px-3 sm:px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 text-sm sm:text-base font-medium transition-smooth shadow-sm"
              >
                {t('landing.nav.getStarted')}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-white">
            <nav className="px-6 py-4 space-y-3">
              <button onClick={() => { scrollToSection('features'); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-muted-foreground hover:text-accent transition-smooth">{t('landing.nav.features')}</button>
              <button onClick={() => { scrollToSection('compliance'); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-muted-foreground hover:text-accent transition-smooth">{t('auditor.tabs.compliance')}</button>
              <button onClick={() => { scrollToSection('pricing'); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-muted-foreground hover:text-accent transition-smooth">{t('landing.nav.pricing')}</button>
              <button onClick={() => { scrollToSection('support'); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-muted-foreground hover:text-accent transition-smooth">{t('loginPage.support')}</button>
              <div className="pt-3 mt-3 border-t border-border space-y-2">
                <button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-foreground hover:text-accent font-medium transition-smooth">
                  {t('landing.nav.signIn')}
                </button>
                <button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }} className="block w-full text-center py-3 px-4 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold transition-smooth shadow-sm">
                  {t('landing.nav.getStarted')}
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <HeroSection />

      {/* Feature Highlights */}
      <div id="features">
        <FeatureHighlights />
      </div>

      {/* Social Proof, also serves as 'Pricing' anchor (see CTA below) */}
      <div id="pricing">
        <SocialProof />
      </div>

      {/* Authentication Call to Action, anchor for Support */}
      <div id="support">
        <AuthCallToAction />
      </div>

      {/* Trust Indicators, anchor for Compliance */}
      <div id="compliance">
        <TrustIndicators />
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <LogoFull variant="white" size="sm" />
              <p className="text-gray-400 text-sm">
                {t('landing.footer.companyTagline')}
              </p>
              <div className="flex space-x-3">
                <Icon name="Mail" size={16} className="text-gray-400" />
                <Icon name="LinkedIn" size={16} className="text-gray-400" />
                <Icon name="Twitter" size={16} className="text-gray-400" />
              </div>
            </div>

            {/* Product Links */}
            <div className="space-y-4">
              <h4 className="font-semibold">{t('landing.footer.product')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <button className="block hover:text-white transition-smooth">{t('landing.footer.product1')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.product2')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.product3')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.product4')}</button>
              </div>
            </div>

            {/* Resources */}
            <div className="space-y-4">
              <h4 className="font-semibold">{t('landing.footer.resourcesSection')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <button className="block hover:text-white transition-smooth">{t('landing.footer.resource1')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.resource2')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.resource3')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.resource4')}</button>
              </div>
            </div>

            {/* Legal */}
            <div className="space-y-4">
              <h4 className="font-semibold">{t('landing.footer.legal')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <button className="block hover:text-white transition-smooth">{t('landing.footer.legal1')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.legal2')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.legal3')}</button>
                <button className="block hover:text-white transition-smooth">{t('landing.footer.legal4')}</button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-400">
              <p>{t('landing.footer.rights', { year: new Date()?.getFullYear() })}</p>
              <p className="mt-1 text-xs text-gray-500">
                ClearEqual Ltd, registered in England and Wales, company number 17310742. Registered office: 167-169 Great Portland Street, 5th Floor, London W1W 5PF.
              </p>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-400 mt-4 md:mt-0">
              <span>🇪🇺 {t('landing.footer.euHosted')}</span>
              <span>{t('landing.footer.gdprBadge')}</span>
              <span>{t('landing.footer.iso27001')}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;